// Cloaking & stealth: tab title/favicon spoof, cloak-on-blur, panic key,
// about:blank embed, anti-close warning.
import { getAll, get } from "../shared/settings-store.js";
import { CLOAK_PRESETS } from "../shared/settings-schema.js";
import { NAME } from "../shared/brand.js";

const BASE_TITLE = NAME;
// Unread Study Hall mentions, shown as "(1) Re:Vault" (app/hall-badge.js). A custom tab title
// the student picked is left exactly as they set it.
let titleCount = 0;
export function setTitleCount(n) {
	const next = Math.max(0, Math.min(99, Number(n) || 0));
	if (next === titleCount) return;
	titleCount = next;
	applyCloak();
}
const BASE_FAVICON = "/icons/favicon.svg";
// Self-hosted, so the cloak never makes a request to Google. Absolute for the about:blank window.
const GOOGLE_FAVICON = location.origin + "/cloaks/google.png";

function setFavicon(href) {
	let l = document.querySelector("link[rel~='icon']");
	if (!l) { l = document.createElement("link"); l.rel = "icon"; document.head.appendChild(l); }
	if (l.getAttribute("href") !== href) l.setAttribute("href", href);
}

function cloakOf() {
	const s = getAll();
	if (s.cloakPreset === "custom") return { title: s.cloakTitle, favicon: s.cloakFavicon };
	const p = CLOAK_PRESETS[s.cloakPreset];
	return p ? { title: p.title, favicon: p.favicon } : { title: "", favicon: "" };
}

export function applyCloak() {
	const c = cloakOf();
	document.title = c.title || (titleCount ? "(" + titleCount + ") " : "") + BASE_TITLE;
	setFavicon(c.favicon || BASE_FAVICON);
}

// about:blank embed (best-effort; pop-ups may require a user gesture).
export function openAboutBlank() {
	try {
		const w = window.open("about:blank", "_blank");
		if (!w) return false;
		const c = cloakOf();
		w.document.title = c.title || "Google";
		const link = w.document.createElement("link");
		link.rel = "icon"; link.href = c.favicon || GOOGLE_FAVICON;
		w.document.head.appendChild(link);
		const ifr = w.document.createElement("iframe");
		ifr.style.cssText = "position:fixed;inset:0;border:0;width:100vw;height:100vh";
		ifr.src = location.href;
		w.document.body.style.margin = "0";
		w.document.body.appendChild(ifr);
		location.replace("https://www.google.com/");
		return true;
	} catch (e) { return false; }
}

export function initCloak() {
	applyCloak();

	window.addEventListener("beforeunload", (e) => {
		if (get("antiClose")) { e.preventDefault(); e.returnValue = ""; }
	});

	window.addEventListener("keydown", (e) => {
		// Don't hijack the key while the user is typing (address bar, page inputs) or
		// mid-IME-composition - otherwise the panic key can't be typed as normal text.
		const el = e.target;
		if (e.isComposing || (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName || "")))) return;
		if (get("panicEnabled") && e.key === (get("panicKey") || "`")) {
			e.preventDefault();
			// Require an absolute http(s) target (the setting's documented intent). Anything
			// else - javascript:/data:/blank/relative - falls back to the default, so a
			// panicUrl smuggled in via an imported backup can't run script on our origin.
			const pu = get("panicUrl") || "https://classroom.google.com";
			location.replace(/^https:\/\//i.test(pu) ? pu : "https://classroom.google.com");
		}
	});

	document.addEventListener("visibilitychange", () => {
		if (!get("cloakOnBlur")) return;
		if (document.hidden) {
			const c = cloakOf();
			document.title = c.title || "Google";
			setFavicon(c.favicon || GOOGLE_FAVICON);
		} else applyCloak();
	});

	if (get("aboutBlank") && window.top === window.self) openAboutBlank();
}
